import Notify from 'quasar/src/plugins/Notify.js';
import notifierClient from '@/libs/notifierClient'
import { shouldDropChannels, shouldIgnoreJourneyPrePause } from '@/libs/systemPause'

let client = null
let clientSession = null
let dismissPrePauseNotice = null

const root = { root: true }
const SET_CONNECTED = 'SET_CONNECTED'
const JOURNEY_PRE_PAUSE = 'JOURNEY_PRE_PAUSE'
const JOURNEY_PRE_PAUSE_CANCELLED = 'JOURNEY_PRE_PAUSE_CANCELLED'

const actions = {
  init (ctx) {
    const isAdmin = ctx.rootGetters['settingsModule/isAdmin']
    if (isAdmin) return

    const token = ctx.rootGetters['agentModule/token'] || ctx.rootGetters['settingsModule/token']
    const customer = ctx.rootGetters['settingsModule/customer']
    const agentId = ctx.rootState?.agentModule?.session?.agent?.id

    if (!token || !customer || !agentId) return
    const session = `${customer}:${agentId}:${token}`
    if (client && clientSession === session) return client.setInterests(getInterests(agentId), true)
    if (client) client.close()

    client = notifierClient({
      customer,
      token,
      interests: getInterests(agentId),
      listeners: {
        [JOURNEY_PRE_PAUSE]: (event) => handleJourneyPrePause(ctx, event),
        [JOURNEY_PRE_PAUSE_CANCELLED]: (event) => handleJourneyPrePauseCancelled(ctx, event)
      },
      onConnect: () => ctx.commit(SET_CONNECTED, true),
      onDisconnect: () => ctx.commit(SET_CONNECTED, false)
    })
    clientSession = session
  },

  finish ({ commit, dispatch }) {
    if (client) client.close()
    client = null
    clientSession = null
    dispatch('agentModule/clearJourneyPrePause', null, root)
    commit(SET_CONNECTED, false)
  }
}

const middlewares = {
  'agentModule/SET_SESSION': (ctx) => {
    const isPausedAgent = shouldDropChannels(ctx.state?.agentModule?.session?.agent)
    const isOfflineAgent = !ctx.state?.agentModule?.session?.agent?.logged
    const shouldClose = isPausedAgent || isOfflineAgent

    if (shouldClose) return ctx.dispatch('notifierModule/finish', null, root)
    ctx.dispatch('notifierModule/init', null, root)
  },

  'agentModule/UPDATE_AGENT': (ctx) => {
    const isPausedAgent = shouldDropChannels(ctx.state?.agentModule?.session?.agent)
    const isOfflineAgent = !ctx.state?.agentModule?.session?.agent?.logged
    const shouldClose = isPausedAgent || isOfflineAgent

    if (shouldClose) return ctx.dispatch('notifierModule/finish', null, root)
    ctx.dispatch('notifierModule/init', null, root)
  },

  'agentModule/SET_JOURNEY_PRE_PAUSE': (_, { payload }) => {
    if (!payload) dismissJourneyPrePauseNotice()
  }
}

const mutations = {
  [SET_CONNECTED]: (state, connected) => {
    state.connected = connected
  }
}

export default {
  namespaced: true,
  actions,
  middlewares,
  mutations,
  state: {
    connected: false
  }
}

const getInterests = (agentId) => ({
  queues: [],
  agents: [String(agentId)],
  extensions: [],
  bots: []
})

const handleJourneyPrePause = (ctx, event) => {
  const { agent, schedulePause } = ctx.rootState?.agentModule?.session ?? {}
  if (shouldIgnoreJourneyPrePause({ agent, schedulePause })) return

  const prePause = event.journeyPrePause ?? {}
  const currentPrePause = ctx.rootState?.agentModule?.journeyPrePause
  if (isSameJourneyPrePause(currentPrePause, prePause)) return

  ctx.dispatch('agentModule/setJourneyPrePause', prePause, root)
  notifyJourneyPrePause(prePause)
}

const isSameJourneyPrePause = (currentPrePause, nextPrePause) => {
  if (!currentPrePause || !nextPrePause) return false

  return [
    'journeyId',
    'journeyPauseId',
    'pauseId',
    'startsAt'
  ].every((field) => String(currentPrePause[field] || '') === String(nextPrePause[field] || ''))
}

const handleJourneyPrePauseCancelled = (ctx, event) => {
  const currentPrePause = ctx.rootState?.agentModule?.journeyPrePause
  if (!currentPrePause) return

  const cancelledJourneyId = event?.journeyPrePause?.journeyId
  if (cancelledJourneyId && String(currentPrePause.journeyId) !== String(cancelledJourneyId)) return

  ctx.dispatch('agentModule/clearJourneyPrePause', null, root)
}

const notifyJourneyPrePause = (prePause) => {
  const noticeMinutes = normalizeNoticeMinutes(prePause.noticeMinutes)
  const message = getJourneyPrePauseMessage(prePause, noticeMinutes)

  dismissJourneyPrePauseNotice()
  dismissPrePauseNotice = Notify.create({
    message: 'Pausa programada',
    caption: message,
    color: 'blue-1',
    textColor: 'blue-8',
    icon: 'mdi-information-outline',
    position: 'bottom-right',
    timeout: noticeMinutes * 60 * 1000,
    multiLine: false,
    progress: true,
    actions: [{ icon: 'mdi-close', color: 'primary' }]
  })
}

const dismissJourneyPrePauseNotice = () => {
  if (typeof dismissPrePauseNotice !== 'function') return
  dismissPrePauseNotice()
  dismissPrePauseNotice = null
}

const normalizeNoticeMinutes = (minutes) => {
  const noticeMinutes = Number(minutes)
  if (!Number.isFinite(noticeMinutes) || noticeMinutes <= 0) return 1
  return Math.floor(noticeMinutes)
}

const getJourneyPrePauseMessage = (prePause, noticeMinutes) => {
  const pauseDescription = prePause.pauseDescription || prePause.description || ''
  const duration = noticeMinutes === 1 ? '1 minuto' : `${noticeMinutes} minutos`
  const fallbackMessage = pauseDescription
    ? `Você tem uma pausa ${pauseDescription} programada, se inicia em ${duration}.`
    : `Você tem uma pausa programada, se inicia em ${duration}.`

  if (!prePause.message) return fallbackMessage
  if (prePause.message.includes(duration)) return prePause.message

  return `${prePause.message} A pausa se inicia em ${duration}.`
}
